import { createSampleFormPayload, FormModel } from '@/models/form';
import FormService from '@/services/formService';
import { refusesEntry } from '@/models/formEntryAccess';
import { canReadFormList } from '@/models/grant';
import { defineStore } from 'pinia';
import { reactive, ref, computed } from 'vue';
import { useStudyStore } from './study';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { LoadingStateModel } from '@/models/loadingState';
import { useRouter } from 'vue-router';
import { createRequestToken } from './requestToken';
import { builderRoute } from '@/components/forms/formRoutes';
import { formOrderPayload } from '@/components/forms/formOrderDraft';
import { translate } from '@/utils/translate';

export const useFormStore = defineStore('form', () => {

  /**
   * The forms list's columns, renumbered in order because `Table` keys each `<Column>` on `id`.
   *
   * Table draws both dates and Fields from their type. FormList's `#bodyContent` draws the other
   * four: Order is the row's place in `formOrderPositions`, Title is a link to the builder, Average
   * entry time carries a tooltip, and Actions holds buttons. Order has no sort: the rows arrive in
   * the form order, and a row holds no field to sort it on.
   *
   * A phone row shows Title, Last edited and Actions; the rest are `mobileHidden` (docs/forms/REVIEW.md
   * F8, decision 148).
   */
  const tableColumns = computed(() => {
    const columns: Omit<TableColumnModel, 'id'>[] = [
      {
        header: 'Order',
        key: 'order',
        type: TableColumnType.TEXT,
        sortable: false,
        mobileHidden: true
      },
      {
        header: 'Title',
        type: TableColumnType.TEXT_WITH_ICON,
        key: 'title',
        sortable: true,
        icon: {
          icon: 'file',
          style: 'fas',
          color: 'var(--ts-primary)'
        }
      },
      {
        header: 'Creation date',
        type: TableColumnType.DATE,
        key: 'createdAt',
        sortable: true,
        mobileHidden: true
      },
      {
        header: 'Last edited',
        type: TableColumnType.DATE,
        key: 'updatedAt',
        sortable: true
      },
      {
        header: 'Fields',
        type: TableColumnType.TEXT,
        key: 'fieldCount',
        sortable: false,
        mobileHidden: true
      },
      ...(studyStore.selectedStudy?.flags?.enableEntryMetrics ? [{
        header: 'Average entry time',
        type: TableColumnType.TEXT,
        key: 'timeSpentData',
        sortable: false,
        mobileHidden: true
      }] : []),
      {
        header: '',
        type: TableColumnType.SQUARE_BUTTON_WITH_ICON,
        key: 'actions',
        sortable: false
      }
    ];

    return columns.map((column, index): TableColumnModel => ({ id: index + 1, ...column }));
  });

  const studyStore = useStudyStore();
  const formList = ref<FormModel[]>([]);
  // The study `formList` holds forms for. It changes when a read for another study starts, which is
  // also the only time the list is blanked, so the list never holds a study's forms other than this
  // one's. `formPermissionValue` reads it, with the loader, before granting all forms.
  const formListStudyId = ref<string>();
  // The forms this study owns, without the shared forms it reads from other studies. Only the forms
  // list's rows read it, so Delete, Clone and Print act on own forms alone. Everything else, the
  // order dialog and form link targets included, reads `formList`.
  const ownForms = computed(() => formList.value.filter(form => !form.sharedForm));
  /**
   * Each form's place in the study's form order, by form id, counting from 1 and counting shared
   * forms. The API sends `formList` in that order, and the place a form reports can disagree with it:
   * a shared form this study never placed reports `null`, a new form 0, and a clone its source's
   * (docs/forms/REVIEW.md F4, decision 56).
   */
  const formOrderPositions = computed(() => new Map(formList.value.map((form, index) => [form.identifier, index + 1])));
  /**
   * The ids of the forms the list says the caller may not enter data on (`FormModel.mayEnterData`), worked out once per
   * list for the pipeline's cells, which each ask about their own form and a requirement's.
   */
  const formsRefusingEntry = computed(() =>
    new Set(formList.value.filter(refusesEntry).map(form => form.identifier)));

  const isGettingForms = reactive(new LoadingStateModel({isLoading: false}));
  // Newest study wins: an answer for a study the list has since moved off is dropped, its failure
  // too. A second ask for the study already in flight shares that read rather than sending another.
  const formsRequest = createRequestToken();
  let inFlight: { studyId: string; read: Promise<void> } | undefined;

  /**
   * Whether the reader's grant in `studyId` says `GET study/:id/forms` would refuse them. Only once
   * that grant has landed: before it, the read goes out. A study switch asks for the forms behind the
   * new study's grant, so it lands first (ENG-10317), unless the grant read fails. Never for staff,
   * whom the read answers whatever their grant (ENG-10320).
   */
  const refusesFormsRead = (studyId: string, options?: { forCase?: boolean }): boolean => {
    const grant = studyStore.grantFor(studyId);
    return grant !== undefined && !canReadFormList(grant, { ...options, staff: studyStore.isStaff });
  };

  /**
   * Read `studyId`'s forms, its own and its shared forms, into `formList`. Writes only the list, its
   * study and its loader: the study store is the one that selects a study. Does nothing without a
   * study, which is what every caller passing `selectedStudyId` before a study is chosen relies on.
   *
   * Nor for a reader whose grant the read would refuse, such as a Manage users holder opening Data:
   * the list is empty for them, and the read's refusal used to be toasted at them (ENG-10306).
   */
  const getStudyForms = (studyId: string | undefined): Promise<void> => {
    if (!studyId) {
      return Promise.resolve();
    }
    if (inFlight?.studyId === studyId) {
      return inFlight.read;
    }
    if (refusesFormsRead(studyId)) {
      // Claimed all the same, so a read still out for the study before lands nothing over this one.
      formsRequest.claim();
      inFlight = undefined;
      formList.value = [];
      formListStudyId.value = studyId;
      isGettingForms.startLoading();
      isGettingForms.stopLoadingAndFinish();
      return Promise.resolve();
    }
    const token = formsRequest.claim();
    if (formListStudyId.value !== studyId) {
      formList.value = [];
      formListStudyId.value = studyId;
    }
    isGettingForms.startLoading();
    const read = (async () => {
      try {
        const forms = await FormService.getStudyForms(studyId);
        if (!formsRequest.holds(token)) return;
        // In the study's form order already; see `FormService.getStudyForms`.
        formList.value = forms;
      } catch (error) {
        if (!formsRequest.holds(token)) return;
        isGettingForms.setError(error);
      } finally {
        if (formsRequest.holds(token)) {
          inFlight = undefined;
          isGettingForms.stopLoadingAndFinish();
        }
      }
    })();
    inFlight = { studyId, read };
    return read;
  };


  const deleteFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  /**
   * Delete `formIds` from `studyId` in one request, and say whether they went. The API deletes all of
   * them or none, so a failure leaves every form in place, on `deleteFormLoader.error` at the inline
   * tier for the delete dialog's footer, with no toast and no re-read. A 409 there names the forms
   * still in use and why, which the dialog lists as blocked (docs/forms/REVIEW.md F5, decision 75).
   */
  const deleteForms = async (studyId: string, formIds: string[]): Promise<boolean> => {
    deleteFormLoader.startLoading();
    try {
      // Counted from the request, which the API deletes whole or not at all.
      await FormService.deleteStudyForms(studyId, formIds);
      deleteFormLoader.setSuccess(translate('forms_delete_success', { count: formIds.length }, formIds.length));
      // Not awaited: the read never rejects, and the list shows its own failure from `isGettingForms`.
      getStudyForms(studyId);
      return true;
    } catch (error) {
      deleteFormLoader.setInlineError(error);
      return false;
    } finally {
      deleteFormLoader.stopLoadingAndFinish();
    }
  };

  const cloneFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  const cloneForms = async (form: FormModel) => {
    const studyId = studyStore.selectedStudyId;
    if (!studyId) {
      return;
    }
    cloneFormLoader.startLoading();
    try {
      await FormService.cloneForm(form.identifier, studyId);
      // Read after the await: a study switched to mid-clone has its own read in flight, which this
      // joins. Re-reading the study the clone started in would claim the list back for it. Awaited so
      // the toast lands with the copy on screen; the read never rejects, it fails on its own loader.
      await getStudyForms(studyStore.selectedStudyId);
      cloneFormLoader.setSuccess(translate('form_clone_success', { title: form.title }));
    } catch (error) {
      cloneFormLoader.setError(error);
    } finally {
      cloneFormLoader.stopLoadingAndFinish();
    }
  };


  const formsOrderLoader = reactive(new LoadingStateModel({isLoading: false}));
  /**
   * Save the Form Order dialog's draft for `studyId`, own and shared forms together, and say whether
   * it saved. A failure is left on `formsOrderLoader.error` at the inline tier, so the dialog shows it
   * in its footer with no toast and keeps the draft open for another try. The API writes every form
   * or none, so a failure has changed nothing.
   *
   * Two admins saving at once: the last save wins for every form, with no version check
   * (docs/forms/REVIEW.md F4, decision 57).
   */
  const saveFormOrder = async (studyId: string, forms: FormModel[]): Promise<boolean> => {
    formsOrderLoader.startLoading();
    try {
      await FormService.saveStudyFormEnforcements(studyId, formOrderPayload(forms));
      formsOrderLoader.setSuccess(translate('form_order_save_success'));
      return true;
    } catch (error) {
      formsOrderLoader.setInlineError(error);
      return false;
    } finally {
      formsOrderLoader.stopLoadingAndFinish();
    }
  };


  const router = useRouter();
  const createFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  const postForm = async () => {
    createFormLoader.startLoading();
    try {
      const postFormResponse = await FormService.postForm(createSampleFormPayload(studyStore.selectedStudyId!));
      getStudyForms(studyStore.selectedStudyId);
      router.push(builderRoute(postFormResponse.identifier));
    } catch (error) {
      createFormLoader.setError(error);
    } finally {
      createFormLoader.stopLoadingAndFinish();
    }
  };

  return {
    getStudyForms,
    refusesFormsRead,
    deleteForms,
    cloneForms,
    saveFormOrder,
    postForm,
    formList,
    ownForms,
    formsRefusingEntry,
    formOrderPositions,
    formListStudyId,
    tableColumns,
    isGettingForms,
    deleteFormLoader,
    cloneFormLoader,
    formsOrderLoader,
    createFormLoader
  };
});
